/**
 * MFA Feature Flags + Phased Enforcement Policy
 *
 * Phased rollout controls. All flags default to a SAFE (non-breaking) state.
 * Existing users continue to log in normally when nothing is configured.
 *
 * Vite env:
 *   VITE_MFA_ENABLED=true|false           Master switch
 *   VITE_MFA_REQUIRED=true|false          Global enforcement (overrides per-role)
 *   VITE_MFA_REQUIRED_ROLES=admin,portfolio_manager:tier_1,...
 *                                          Roles (optionally tier-scoped) under enforcement
 *   VITE_MFA_GRACE_DAYS=14                Legacy single grace (fallback)
 *   VITE_MFA_DEPLOYMENT_DATE=2026-05-27   Anchor for existing users
 *   VITE_MFA_EMERGENCY_BYPASS=email1,...  Break-glass allowlist
 *
 * Per-role grace policy (days) — overrides VITE_MFA_GRACE_DAYS when matched:
 *   admin                              → 0   (immediate)
 *   portfolio_manager:tier_2           → 7
 *   portfolio_manager:tier_1           → 14
 *   reviewer / master_reviewer         → 30
 *   respondent                         → 30
 *   client_admin                       → 30
 */

// NOTE: PREVIEW_ALLOWLIST (report-preview gating) is intentionally NOT imported
// here. MFA emergency bypass must be sourced exclusively from
// VITE_MFA_EMERGENCY_BYPASS so that adding an email to the preview allowlist
// can never silently disable MFA enforcement for that user.

function parseBool(v: string | undefined, fallback: boolean): boolean {
  if (v === undefined || v === "") return fallback;
  return v === "true" || v === "1" || v === "yes";
}

function parseList(v: string | undefined): string[] {
  if (!v) return [];
  return v.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
}

function parseInt0(v: string | undefined, fallback: number): number {
  if (!v) return fallback;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function parseDate(v: string | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export const MFA_ENABLED: boolean = parseBool(
  import.meta.env.VITE_MFA_ENABLED as string | undefined,
  true,
);

export const MFA_REQUIRED: boolean = parseBool(
  import.meta.env.VITE_MFA_REQUIRED as string | undefined,
  false,
);

export const MFA_REQUIRED_ROLES: string[] = parseList(
  import.meta.env.VITE_MFA_REQUIRED_ROLES as string | undefined,
);

export const MFA_GRACE_DAYS: number = parseInt0(
  import.meta.env.VITE_MFA_GRACE_DAYS as string | undefined,
  14,
);

/** Anchor used for users created BEFORE this date. Falls back to "now" if unset. */
export const MFA_DEPLOYMENT_DATE: Date | null = parseDate(
  import.meta.env.VITE_MFA_DEPLOYMENT_DATE as string | undefined,
);

const envBypass = parseList(import.meta.env.VITE_MFA_EMERGENCY_BYPASS as string | undefined);
export const EMERGENCY_BYPASS_EMAILS: string[] = Array.from(new Set(envBypass));

/** Per-role grace policy. Lower = stricter. Used by `roleGraceDays()`. */
export const ROLE_GRACE_DAYS: Array<{ role: string; tier?: string; days: number }> = [
  { role: "admin", days: 0 },
  { role: "portfolio_manager", tier: "tier_2", days: 7 },
  { role: "portfolio_manager", tier: "tier_1", days: 14 },
  { role: "portfolio_manager", days: 14 }, // default PM
  { role: "master_reviewer", days: 30 },
  { role: "reviewer", days: 30 },
  { role: "client_admin", days: 30 },
  { role: "respondent", days: 30 },
];

/**
 * Resolve the strictest (lowest) grace-days value for a user's roles+tier.
 * Returns null when the user has no role that triggers enforcement.
 */
export function roleGraceDays(roles: string[], tier?: string | null): number | null {
  const r = roles.map((x) => x.toLowerCase());
  const t = (tier ?? "").toLowerCase();
  let strictest: number | null = null;
  for (const policy of ROLE_GRACE_DAYS) {
    if (!r.includes(policy.role)) continue;
    if (policy.tier && policy.tier !== t) continue;
    if (strictest === null || policy.days < strictest) strictest = policy.days;
  }
  return strictest;
}

/**
 * Decide whether the given user must enforce MFA at all (independent of timing).
 * FAIL OPEN: any unexpected input returns false.
 */
export function mustEnforceMfa(opts: {
  email?: string | null;
  roles?: string[];
  tier?: string | null;
}): boolean {
  try {
    if (!MFA_ENABLED) return false;
    const email = (opts.email ?? "").toLowerCase();
    if (email && EMERGENCY_BYPASS_EMAILS.includes(email)) return false;
    if (MFA_REQUIRED) return true;

    const roles = (opts.roles ?? []).map((r) => r.toLowerCase());
    const tier = (opts.tier ?? "").toLowerCase();

    // 1. Explicit env list (legacy)
    for (const requirement of MFA_REQUIRED_ROLES) {
      const [reqRole, reqTier] = requirement.split(":").map((s) => s.trim().toLowerCase());
      if (!reqRole || !roles.includes(reqRole)) continue;
      if (!reqTier || tier === reqTier) return true;
    }

    // 2. Built-in role policy table (always-on when role grace matches)
    return roleGraceDays(roles, tier) !== null;
  } catch (err) {
    console.error("[mfaFlags] mustEnforceMfa failed — failing OPEN", err);
    return false;
  }
}

/**
 * Compute the timestamp at which MFA becomes mandatory for the user.
 *  - anchor = max(deployment_date, account_created_at)
 *    → existing users get grace from deploy date
 *    → new users get grace from account creation
 *  - required_after = anchor + roleGraceDays
 * Returns null if MFA isn't required for this user at all.
 */
export function computeMfaRequiredAfter(opts: {
  email?: string | null;
  roles: string[];
  tier?: string | null;
  accountCreatedAt: Date | null;
}): Date | null {
  if (!mustEnforceMfa({ email: opts.email, roles: opts.roles, tier: opts.tier })) return null;
  const days = roleGraceDays(opts.roles, opts.tier) ?? MFA_GRACE_DAYS;
  const deploy = MFA_DEPLOYMENT_DATE ?? new Date();
  const created = opts.accountCreatedAt ?? deploy;
  const anchorMs = Math.max(deploy.getTime(), created.getTime());
  return new Date(anchorMs + days * 24 * 3600 * 1000);
}
